/**
 * Built-in stockpile materials with bulk densities (kg/m³).
 *
 * Densities are loose / pile-ready bulk values, not solid-rock densities, since
 * stockpile volumes measure the apparent pile (with voids) rather than the
 * compacted solid. Sources:
 *
 *  - Sand (loose / compacted), gravel, crushed stone, topsoil:
 *    Engineering Toolbox — "Densities of Common Materials"
 *    (https://www.engineeringtoolbox.com/density-materials-d_1652.html).
 *  - Coal (anthracite): USGS — "Coal Density" technical note.
 *  - Snow (fresh): NSIDC — "Snow Density" (range 70–150 kg/m³, midpoint 100).
 *  - Water: 1000 kg/m³ at 4 °C (canonical).
 *
 * "Custom" lets the user enter their own value. Densities are ROUGH bulk
 * defaults — for accurate mass figures the surveyor should override.
 */

export type MaterialId =
  | 'none'
  | 'sand-loose'
  | 'sand-compacted'
  | 'gravel'
  | 'crushed-stone'
  | 'coal-anthracite'
  | 'topsoil'
  | 'snow-fresh'
  | 'water'
  | 'custom';

export interface Material {
  /** Stable ID — used in form state and persisted Feature properties. */
  id: MaterialId;
  /** Translation key under `stockpile.materials.*`. */
  labelKey: string;
  /** Bulk density in kg/m³. `null` means user-entered. */
  densityKgPerM3: number | null;
}

export const MATERIALS: Material[] = [
  { id: 'none', labelKey: 'none', densityKgPerM3: null },
  { id: 'sand-loose', labelKey: 'sandLoose', densityKgPerM3: 1500 },
  { id: 'sand-compacted', labelKey: 'sandCompacted', densityKgPerM3: 1700 },
  { id: 'gravel', labelKey: 'gravel', densityKgPerM3: 1680 },
  { id: 'crushed-stone', labelKey: 'crushedStone', densityKgPerM3: 1600 },
  { id: 'coal-anthracite', labelKey: 'coalAnthracite', densityKgPerM3: 1100 },
  { id: 'topsoil', labelKey: 'topsoil', densityKgPerM3: 1300 },
  { id: 'snow-fresh', labelKey: 'snowFresh', densityKgPerM3: 100 },
  { id: 'water', labelKey: 'water', densityKgPerM3: 1000 },
  { id: 'custom', labelKey: 'custom', densityKgPerM3: null },
];

export const MATERIAL_BY_ID = new Map(MATERIALS.map((m) => [m.id, m]));

export function getMaterial(id: MaterialId): Material {
  return MATERIAL_BY_ID.get(id) ?? MATERIALS[0];
}

/**
 * Resolve the effective density for a material selection. For 'custom', returns
 * the user-supplied value (or null when unset/invalid). For built-ins, returns
 * the canonical density. For 'none', returns null.
 */
export function resolveDensity(materialId: MaterialId, customKgPerM3: number | null): number | null {
  if (materialId === 'none') return null;
  if (materialId === 'custom') {
    if (customKgPerM3 == null || !Number.isFinite(customKgPerM3) || customKgPerM3 <= 0) return null;
    return customKgPerM3;
  }
  return getMaterial(materialId).densityKgPerM3;
}
